import React, { ReactElement } from "react"

import { Tooltip } from "@chakra-ui/core"
import { faFrown, faMeh, faSmile } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid"

import { AlertLevel, Color, Size } from "~tailwindui/types/enums"

import classNames from "src/classNames"

export const ratingIcon = (mastery: "weak" | "moderate" | "strong") => {
  let icon: React.ReactElement

  switch (mastery) {
    case "weak":
      icon = <FontAwesomeIcon icon={faFrown} className="text-red-500" />
      break
    case "moderate":
      icon = <FontAwesomeIcon icon={faMeh} className="text-orange-500" />
      break
    case "strong":
      icon = <FontAwesomeIcon icon={faSmile} className="text-green-600" />
      break
    default:
      throw new Error("This mastery level is not supported")
  }

  return (
    <Tooltip
      label={mastery}
      className="rounded-md bg-gray-600 py-1 px-2 text-white"
      aria-label={mastery}
    >
      <span className="text-4xl">{icon}</span>
    </Tooltip>
  )
}

export const chipIconColorClassNames = (color: Color) => {
  switch (color) {
    case Color.Blue:
      return "bg-blue-500"
    case Color.Sky:
      return "bg-sky-500"
    case Color.Orange:
      return "bg-orange-500"
    case Color.Red:
      return "bg-red-500"
    case Color.Green:
      return "bg-green-500"
    default:
      throw new Error("This color is not supported")
  }
}

export const buttonClassNames = ({
  size = Size.Medium,
  color = Color.Blue,
  outline = false,
  soft = false,
  disabled = false,
}: {
  size?: Size
  color?: Color
  outline?: boolean
  soft?: boolean
  disabled?: boolean
}): string => {
  return classNames(
    "font-medium tracking-wide shadow-sm text-center max-h-max",
    disabled ? "cursor-not-allowed" : "cursor-pointer",
    buttonSizeClassNames(size),
    disabled
      ? "text-white bg-gray-300"
      : outline
      ? "bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      : soft
      ? softButtonColorClassNames(color)
      : buttonColorClassNames(color)
  )
}

export const buttonSizeClassNames = (size: Size) => {
  switch (size) {
    case Size.Small:
      return "rounded px-2 py-1 text-base"
    case Size.Medium:
      return "rounded-md px-2.5 py-1.5 text-lg"
    case Size.Large:
      return "rounded-md px-3.5 py-2.5 text-lg"
    default:
      throw new Error("This size is not supported")
  }
}

export const buttonColorClassNames = (color: Color) => {
  switch (color) {
    case Color.Blue:
      return "bg-blue-500 text-white hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
    case Color.Sky:
      return "bg-sky-400 text-white hover:bg-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400"
    case Color.Red:
      return "bg-red-500 text-white hover:bg-red-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
    case Color.Green:
      return "bg-green-600 text-white hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
    case Color.Orange:
      return "bg-orange-400 text-white hover:bg-orange-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
    case Color.Teal:
      return "bg-teal-600 text-white hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
    case Color.Gray:
      return "bg-gray-500 text-white hover:bg-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
    case Color.Purple:
      return "bg-purple-500 text-white hover:bg-purple-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
    default:
      throw new Error("This color is not supported")
  }
}

export const softButtonColorClassNames = (color: Color) => {
  switch (color) {
    case Color.Blue:
      return "bg-blue-100 text-blue-600 hover:bg-blue-100"
    case Color.Sky:
      return "bg-sky-100 text-sky-600 hover:bg-sky-100"
    case Color.Red:
      return "bg-red-100 text-red-600 hover:bg-red-100 hover:bg-red-200"
    case Color.Green:
      return "bg-green-100 text-green-600 hover:bg-green-100 hover:bg-green-200"
    case Color.Orange:
      return "bg-orange-100 text-orange-600 hover:bg-orange-100 hover:bg-orange-200"
    case Color.Teal:
      return "bg-teal-100 text-teal-600 hover:bg-teal-100 hover:bg-teal-200"
    case Color.Gray:
      return "bg-gray-100 text-gray-600 hover:bg-gray-100 hover:bg-gray-200"
    case Color.Purple:
      return "bg-purple-100 text-purple-600 hover:bg-purple-100 hover:bg-purple-200"
    default:
      throw new Error("This color is not supported")
  }
}

export const backgroundColorClassNames = (color: Color) => {
  switch (color) {
    case Color.Red:
      return "bg-red-100"
    case Color.Teal:
      return "bg-teal-100"
    case Color.Blue:
      return "bg-blue-100"
    case Color.Sky:
      return "bg-sky-100"
    case Color.Green:
      return "bg-green-100"
    case Color.Purple:
      return "bg-purple-100"
    case Color.Orange:
      return "bg-orange-100"
    default:
      throw new Error("This color is not supported")
  }
}

export const textColorClassNames = ({
  color,
  dark = false,
}: {
  color: Color
  dark?: boolean
}) => {
  switch (color) {
    case Color.Red:
      return dark ? "text-red-900" : "text-red-700"
    case Color.Blue:
      return dark ? "text-blue-900" : "text-blue-700"
    case Color.Sky:
      return dark ? "text-sky-900" : "text-sky-700"
    case Color.Teal:
      return dark ? "text-teal-900" : "text-teal-700"
    case Color.Green:
      return dark ? "text-green-900" : "text-green-700"
    case Color.Purple:
      return dark ? "text-purple-900" : "text-purple-700"
    case Color.Orange:
      return dark ? "text-orange-900" : "text-orange-700"
    default:
      throw new Error("This color is not supported")
  }
}

export const alertBackgroundClassNames = (level: AlertLevel) => {
  switch (level) {
    case AlertLevel.Error:
      return backgroundColorClassNames(Color.Red)
    case AlertLevel.Warning:
      return backgroundColorClassNames(Color.Orange)
    case AlertLevel.Info:
      return backgroundColorClassNames(Color.Blue)
    case AlertLevel.Success:
      return backgroundColorClassNames(Color.Green)
    default:
      throw new Error("This level is not supported")
  }
}

export const alertTextClassNames = (level: AlertLevel) => {
  switch (level) {
    case AlertLevel.Error:
      return textColorClassNames({ color: Color.Red, dark: true })
    case AlertLevel.Warning:
      return textColorClassNames({ color: Color.Orange, dark: true })
    case AlertLevel.Info:
      return textColorClassNames({ color: Color.Blue, dark: true })
    case AlertLevel.Success:
      return textColorClassNames({ color: Color.Green, dark: true })
    default:
      throw new Error("This level is not supported")
  }
}

export const alertIcon = (level: AlertLevel): ReactElement<any, any> => {
  switch (level) {
    case AlertLevel.Error:
      return <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
    case AlertLevel.Warning:
      return (
        <ExclamationTriangleIcon
          className="h-5 w-5 text-orange-400"
          aria-hidden="true"
        />
      )
    case AlertLevel.Info:
      return (
        <InformationCircleIcon
          className="h-5 w-5 text-sky-400"
          aria-hidden="true"
        />
      )
    case AlertLevel.Success:
      return (
        <CheckCircleIcon
          className="h-5 w-5 text-green-400"
          aria-hidden="true"
        />
      )
    default:
      throw new Error("This level is not supported")
  }
}
